import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import axiosConfig from "../../AxiosConfig";
import { Button } from "reactstrap";

const REPORTS = {
  OPENING: "opening",
  INWARD: "inward",
  OUTWARD: "outward",
  CLOSING: "closing",
};

const REPORT_TITLES = {
  opening: "OPENING STOCK REPORT",
  inward: "INWARD STOCK REPORT",
  outward: "OUTWARD STOCK REPORT",
  closing: "CLOSING STOCK REPORT",
};

const TABLE_COLUMNS = {
  opening: [
    {
      label: "Product Name",
      key: "Product_Title",
    },
    {
      label: "Opening Qty",
      key: "oQty",
    },
    {
      label: "Opening Rate",
      key: "openingRate",
    },
    {
      label: "Opening Total",
      key: "openingTotal",
    },
  ],

  inward: [
    {
      label: "Product Name",
      key: "Product_Title",
    },
    {
      label: "Inward Qty",
      key: "iQty",
    },
    {
      label: "Inward Total",
      key: "inwardTotal",
    },
  ],

  outward: [
    {
      label: "Product Name",
      key: "Product_Title",
    },
    {
      label: "Outward Qty",
      key: "outwardQty",
    },
  ],

  closing: [
    {
      label: "Product Name",
      key: "Product_Title",
    },
    {
      label: "Closing Qty",
      key: "closingQty",
    },
    {
      label: "Closing Rate",
      key: "closingRate",
    },
    {
      label: "Closing Total",
      key: "closingTotal",
    },
  ],
};

export default function StockReport() {

  const [activeReport, setActiveReport] =
    useState(REPORTS.OPENING);
  const [hideZeroQty, setHideZeroQty] =
    useState(false);
  const [data, setData] = useState([]);

  const [loading, setLoading] =
    useState(false);

  const [search, setSearch] =
    useState("");

  const [summary, setSummary] =
    useState({});

  // ================= FETCH REPORT =================

  const fetchReport = async () => {

    try {

      setLoading(true);

      const userData = JSON.parse(
        localStorage.getItem("userData")
      ) || {};

      const database =
        userData?.database;

      // ✅ CURRENT FINANCIAL YEAR

      const currentDate = new Date();

      const currentYear =
        currentDate.getFullYear();

      const nextYear =
        currentYear + 1;

      const month =
        currentDate.getMonth() + 1;

      const financialYear =
        month >= 4
          ? `${currentYear}-${nextYear}`
          : `${currentYear - 1}-${currentYear}`;

      // ✅ API
      const res =
        await axiosConfig.get(
          `/stock-updation/view-stock-report/${database}/${financialYear}`
        );
      console.log(
        "STOCK REPORT =>",
        res?.data
      );

      setData(
        res?.data?.data || []
      );

      setSummary(
        res?.data?.totalSummary || {}
      );

    } catch (err) {

      console.log(
        "REPORT ERROR =>",
        err
      );

      setData([]);

    } finally {

      setLoading(false);

    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  // ================= SEARCH FILTER =================
  const filteredData = useMemo(() => {

    return data.filter((item) => {

      // report wise qty key
      const qtyKey =
        activeReport === "opening"
          ? "oQty"
          : activeReport === "inward"
            ? "iQty"
            : activeReport === "outward"
              ? "outwardQty"
              : "closingQty";

      const qty =
        Number(item?.[qtyKey]) || 0;

      const matchesSearch =
        item?.Product_Title
          ?.toLowerCase()
          ?.includes(
            search.toLowerCase()
          );

      // ✅ hide/show zero qty
      if (hideZeroQty) {
        return qty !== 0 && matchesSearch;
      }

      return matchesSearch;

    });

  }, [
    data,
    search,
    activeReport,
    hideZeroQty,
  ]);

  // ================= TOTAL KEY =================

  const TOTAL_KEYS = {
    opening: "openingTotal",
    inward: "inwardTotal",
    outward: "outwardQty",
    closing: "closingTotal",
  };

  // ================= REPORT TOTAL =================

  const reportTotal = useMemo(() => {

    const key =
      TOTAL_KEYS[activeReport];

    return filteredData.reduce(
      (sum, row) => {

        const value = Number(
          row[key]
        );

        return (
          sum +
          (isNaN(value)
            ? 0
            : value)
        );

      },
      0
    );

  }, [filteredData, activeReport]);

  // ================= FORMAT NUMBER =================

  const formatNumber = (
    value,
    digits = 2
  ) => {

    const num = Number(value);

    if (isNaN(num)) return 0;

    return Number(
      num.toFixed(digits)
    );

  };

  return (

    <div
      className="erp-page-grid"
      style={{
        padding: "15px",
      }}
    >

      {/* ================= TOP BAR ================= */}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          marginBottom: "15px",
          flexWrap: "wrap",
        }}
      >

        {/* REPORT TYPE */}

        <select
          value={activeReport}
          onChange={(e) =>
            setActiveReport(
              e.target.value
            )
          }
          style={{
            padding: "8px",
          }}
        >

          <option
            value={
              REPORTS.OPENING
            }
          >
            Opening Stock
          </option>

          <option
            value={
              REPORTS.INWARD
            }
          >
            Inward Stock
          </option>

          <option
            value={
              REPORTS.OUTWARD
            }
          >
            Outward Stock
          </option>

          <option
            value={
              REPORTS.CLOSING
            }
          >
            Closing Stock
          </option>

        </select>

        {/* TITLE */}

        <h3
          style={{
            margin: 0,
          }}
        >
          {
            REPORT_TITLES[
            activeReport
            ]
          }
        </h3>

        <div
          style={{
            flex: 1,
          }}
        />

        {/* TOTAL */}

        <div
          style={{
            fontWeight: "bold",
            fontSize: "14px",
          }}
        >

          Total :
          {" "}
          ₹
          {" "}
          {
            formatNumber(
              reportTotal
            )
          }

        </div>

        {/* SEARCH */}

        <input
          type="text"
          placeholder="Search Product"
          value={search}
          onChange={(e) =>
            setSearch(
              e.target.value
            )
          }
          style={{
            padding: "8px",
            width: "220px",
          }}
        />
        <Button
          color={
            hideZeroQty
              ? "danger"
              : "primary"
          }
          onClick={() =>
            setHideZeroQty(
              !hideZeroQty
            )
          }
        >
          {hideZeroQty
            ? "Show 0 Qty"
            : "Hide 0 Qty"}
        </Button>
      </div>

      {/* ================= TABLE ================= */}

      <div
        style={{
          overflowX: "auto",
        }}
      >

        <table
          className="table table-bordered"
          style={{
            width: "100%",
            borderCollapse:
              "collapse",
          }}
        >

          <thead
            style={{
              background:
                "#e4e8ea",
            }}
          >

            <tr>

              {
                TABLE_COLUMNS[
                  activeReport
                ].map(
                  (col, index) => (

                    <th
                      key={index}
                      style={{
                        textAlign:
                          "center",
                        padding:
                          "10px",
                      }}
                    >

                      {col.label}

                    </th>

                  )
                )
              }

            </tr>

          </thead>

          <tbody>

            {loading ? (

              <tr>

                <td
                  colSpan={
                    TABLE_COLUMNS[
                      activeReport
                    ].length
                  }
                  style={{
                    textAlign:
                      "center",
                  }}
                >
                  Loading...
                </td>

              </tr>

            ) : filteredData.length === 0 ? (

              <tr>

                <td
                  colSpan={
                    TABLE_COLUMNS[
                      activeReport
                    ].length
                  }
                  style={{
                    textAlign:
                      "center",
                  }}
                >
                  No Data Found
                </td>

              </tr>

            ) : (

              <>

                {/* DATA ROW */}

                {
                  filteredData.map(
                    (
                      row,
                      index
                    ) => (

                      <tr
                        key={index}
                      >

                        {
                          TABLE_COLUMNS[
                            activeReport
                          ].map(
                            (
                              col,
                              i
                            ) => (

                              <td
                                key={i}
                                style={{
                                  textAlign:
                                    "center",
                                  padding:
                                    "8px",
                                }}
                              >

                                {
                                  formatNumber(
                                    row[
                                    col
                                      .key
                                    ]
                                  ) ||
                                  row[
                                  col
                                    .key
                                  ]
                                }

                              </td>

                            )
                          )
                        }

                      </tr>

                    )
                  )
                }

                {/* TOTAL ROW */}

                <tr
                  style={{
                    background:
                      "#f1f1f1",
                    fontWeight:
                      "bold",
                  }}
                >

                  {
                    TABLE_COLUMNS[
                      activeReport
                    ].map(
                      (
                        col,
                        index
                      ) => (

                        <td
                          key={index}
                          style={{
                            textAlign:
                              "center",
                            padding:
                              "8px",
                          }}
                        >

                          {index === 0
                            ? "TOTAL"
                            : formatNumber(
                              summary[
                              col
                                .key
                              ]
                            )}

                        </td>

                      )
                    )
                  }

                </tr>

              </>

            )}

          </tbody>

        </table>

      </div>

    </div>

  );

}